// Pure diff between two brief version snapshots (append-only rows from
// brief_versions). Powers the admin amend change-summary, the version-history
// diff (list + side-by-side), and the supplier-facing change markers. Briefs
// have no line items / totals (unlike responses), so this is a sibling of
// responseDiff: scalar fields + the body + documents. Returns raw values;
// the view formats currency/dates. Body diff now produces word-level
// segments too — see diffBodySegments below.

import { diffWordsWithSpace } from 'diff';

const norm = (s) => (s == null ? '' : String(s)).trim();
// Brief bodies are Quill HTML or plain text; compare on stripped text so
// markup-only churn doesn't read as a change.
const stripText = (s) => norm(s).replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();

// Word-level body diff. Returns an array of segments shaped like
//   { value, added, removed }
// where `added`/`removed` are mutually-exclusive booleans (both false
// for unchanged context). Operates on the stripped text — Quill markup
// is normalised away before comparison so formatting-only changes
// don't create noisy diffs (per the redesign brief, ignore formatting
// in v1).
//
// Returns an empty array when the two bodies normalise to the same
// text — callers should branch on that rather than always rendering
// the segment list.
export function diffBodySegments(prevHtml, currHtml) {
  const a = stripText(prevHtml);
  const b = stripText(currHtml);
  if (a === b) return [];
  return diffWordsWithSpace(a, b).map(part => ({
    value: part.value,
    added: !!part.added,
    removed: !!part.removed,
  }));
}

// label + type drive how the view renders each field's was/now.
// brief_type is an enum (NULL / 'exclusive' / 'competitive'); the 'enum'
// type tells the view to render via humaniseBriefType below so the diff
// reads as "Don't indicate → Exclusive" rather than the raw column value.
// Field order mirrors the AddBrief creation form so the Amend panel
// can render its grid in the same sequence and the diff list reads
// the way an operator scans the source-of-truth form.
const FIELD_DEFS = [
  { key: 'fusion_project_number', label: 'Project Number', type: 'text' },
  { key: 'client', label: 'Client', type: 'text' },
  { key: 'fusion_project_name', label: 'Project Name', type: 'text' },
  { key: 'brief_name', label: 'Brief Name', type: 'text' },
  { key: 'brief_category', label: 'Category', type: 'text' },
  { key: 'nda_required', label: 'NDA Required', type: 'bool' },
  { key: 'brief_type', label: 'Brief Type', type: 'enum' },
  { key: 'required_response_time', label: 'Response Deadline', type: 'datetime' },
  { key: 'event_live_date', label: 'Event Live Start', type: 'date' },
  { key: 'event_live_end_date', label: 'Event Live End', type: 'date' },
  // 2026-06: Budget split into Min + Max. Both diff-tracked
  // independently so the amend pill lights up correctly when only
  // one side changes. Legacy briefs with only maximum_budget set
  // diff as "Maximum Budget: £X → £Y" exactly as before.
  { key: 'minimum_budget', label: 'Minimum Budget', type: 'currency' },
  { key: 'maximum_budget', label: 'Maximum Budget', type: 'currency' },
  { key: 'location', label: 'Location', type: 'text' },
  { key: 'expected_required_by_date', label: 'Access from', type: 'date' },
  { key: 'delivery_access_to_date', label: 'Access to', type: 'date' },
  { key: 'delivery_address', label: 'Access Address', type: 'text' },
  // 2026-06: "Rig" terminology renamed to "Install" in user-facing
  // labels per the brief-detail copy update. DB column names
  // (rig_required, rig_start_date, etc.) are unchanged — only the
  // human-readable labels shift. Any consumer that looks up changes
  // by label (e.g. AmendBriefPanel's changedField('Install Required'))
  // must use the new label string. De-rig kept as-is (separate concept;
  // the user only renamed the install side).
  { key: 'rig_required', label: 'Install Required', type: 'bool' },
  { key: 'rig_start_date', label: 'Install Start', type: 'datetime' },
  { key: 'rig_end_date', label: 'Install End', type: 'datetime' },
  { key: 'rig_details', label: 'Install Details', type: 'text' },
  { key: 'derig_required', label: 'De-rig Required', type: 'bool' },
  { key: 'derig_start_date', label: 'De-rig Start', type: 'datetime' },
  { key: 'derig_end_date', label: 'De-rig End', type: 'datetime' },
  { key: 'derig_details', label: 'De-rig Details', type: 'text' },
];

// Renders the brief_type enum for diff display. The DB stores null /
// 'exclusive' / 'competitive'; the change summary shows the human label.
export const humaniseBriefType = (v) => {
  if (v === 'exclusive') return 'Exclusive';
  if (v === 'competitive') return 'Competitive';
  return "Don't indicate";
};

// Pretty-print a nullable boolean for the diff rendering paths. NULL /
// false both read as "No"; true reads as "Yes". Used by FIELD_DEFS
// entries with type:'bool' (rig_required, derig_required, nda_required).
export const humaniseBool = (v) => (v ? 'Yes' : 'No');

function diffFields(prev, curr) {
  const out = [];
  for (const f of FIELD_DEFS) {
    const was = prev?.[f.key] ?? null;
    const now = curr?.[f.key] ?? null;
    let same;
    if (f.type === 'currency') {
      same = (Number(was) || null) === (Number(now) || null);
    } else if (f.type === 'bool') {
      // Coerce nullable booleans so "null" (legacy unset) compares
      // equal to false — otherwise enabling a rig toggle that was
      // previously null on an old brief surfaces as a phantom change.
      same = (!!was) === (!!now);
    } else {
      same = norm(was) === norm(now);
    }
    if (!same) out.push({ field: f.label, type: f.type, was, now });
  }
  return out;
}

function diffDocuments(prev, curr) {
  const docsOf = (v) => (Array.isArray(v?.document_refs) ? v.document_refs : []);
  const key = (d) => norm(d.display_name) || norm(d.file_name) || norm(d.file_url);
  const prevDocs = docsOf(prev);
  const currDocs = docsOf(curr);
  const prevKeys = new Set(prevDocs.map(key));
  const currKeys = new Set(currDocs.map(key));
  const rows = [];
  for (const d of currDocs) if (!prevKeys.has(key(d))) rows.push({ tag: 'add', name: d.display_name || d.file_name });
  for (const d of prevDocs) if (!currKeys.has(key(d))) rows.push({ tag: 'rem', name: d.display_name || d.file_name });
  return rows;
}

// Diff `curr` against the version immediately before it (`prev`). prev null =
// the original version (nothing to compare).
export function diffBriefVersions(prev, curr) {
  if (!curr) return null;
  if (!prev) return { isOriginal: true, fields: [], body: { was: null, now: curr.brief_content ?? null, changed: false }, documents: [] };
  return {
    isOriginal: false,
    fields: diffFields(prev, curr),
    body: {
      was: prev.brief_content ?? null,
      now: curr.brief_content ?? null,
      changed: stripText(prev.brief_content) !== stripText(curr.brief_content),
    },
    documents: diffDocuments(prev, curr),
  };
}

export function hasBriefChanges(diff) {
  if (!diff || diff.isOriginal) return false;
  return diff.fields.length > 0 || diff.body.changed || diff.documents.length > 0;
}

// Set of changed field labels (+ 'body') for quick "is this field changed?"
// checks in the supplier inline markers.
export function changedKeys(diff) {
  const s = new Set();
  if (!diff || diff.isOriginal) return s;
  for (const f of diff.fields) s.add(f.field);
  if (diff.body.changed) s.add('body');
  return s;
}

// ============================================================================
// Consequence flags (review-brief-amendments §3)
// ============================================================================
// A supplier reading a list of changes needs to know which ones plausibly move
// their PRICE or their WORK, and — the brief is explicit about this — the flags
// must NOT all look the same: "a date ≠ a 2.1t load".
//
// Derived from the field, not written by the amender (Rob's open question 2):
// an amender marking their own changes as unimportant is exactly the failure
// this is meant to catch, and the field type already tells us most of it.
//
// Three levels:
//   'price'    almost certainly changes the number they quoted — money, scope,
//              load, quantity, or a new requirement they hadn't priced
//   'schedule' changes WHEN they must do it — feasible or not, and often a
//              crew-cost question even when the work is identical
//   null       context: names, references, addresses of record
const CONSEQUENCE_BY_LABEL = {
  'Minimum Budget': 'price',
  'Maximum Budget': 'price',
  'NDA Required': 'price',
  'Brief Type': 'price',
  'Category': null,
  'Response Deadline': 'schedule',
  'Event Live Start': 'schedule',
  'Event Live End': 'schedule',
  'Access from': 'schedule',
  'Access to': 'schedule',
  'Location': 'price',
  'Access Address': 'price',
};

// Anything matching these in a label is scope/logistics, whatever the field is
// called — new fields shouldn't silently default to "no consequence".
const PRICE_PATTERNS = [/rig/i, /install/i, /de-?rig/i, /load/i, /weight/i, /dimension/i, /quantity/i, /spec/i, /crew/i, /power/i];
const SCHEDULE_PATTERNS = [/date/i, /time/i, /deadline/i, /access/i, /start/i, /end/i];

export function consequenceOf(label) {
  if (Object.prototype.hasOwnProperty.call(CONSEQUENCE_BY_LABEL, label)) return CONSEQUENCE_BY_LABEL[label];
  const s = String(label ?? '');
  if (PRICE_PATTERNS.some(re => re.test(s))) return 'price';
  if (SCHEDULE_PATTERNS.some(re => re.test(s))) return 'schedule';
  return null;
}

export const CONSEQUENCE_LABEL = {
  price: 'May affect your price',
  schedule: 'Changes the timings',
};

// The change list the supplier's changes view renders: one row per change,
// each with what it was, what it is now, and whether it plausibly matters.
// Body and document changes are included as rows so nothing is only visible by
// scrolling the marked-up brief.
export function changeRows(diff) {
  if (!diff || diff.isOriginal) return [];
  const rows = diff.fields.map(f => ({
    kind: 'field',
    label: f.field,
    type: f.type,
    was: f.was,
    now: f.now,
    consequence: consequenceOf(f.field),
  }));
  if (diff.body?.changed) {
    // The brief text is where scope actually lives, so it always carries the
    // price flag — we can't tell a typo fix from a new requirement.
    rows.push({ kind: 'body', label: 'The brief itself', consequence: 'price' });
  }
  for (const d of diff.documents || []) {
    rows.push({
      kind: 'document',
      label: d.tag === 'add' ? 'Document added' : 'Document removed',
      name: d.name,
      tag: d.tag,
      consequence: d.tag === 'add' ? 'price' : null,
    });
  }
  // Price first, then schedule, then context — the supplier should hit the
  // expensive news before the cosmetic news.
  const rank = { price: 0, schedule: 1 };
  return rows.sort((a, b) => (rank[a.consequence] ?? 2) - (rank[b.consequence] ?? 2));
}

// "3 changes · including the load spec" — the banner row text (§ banner rules:
// rows must name WHAT changed, not just how many).
export function changeSummaryText(diff) {
  const rows = changeRows(diff);
  if (rows.length === 0) return null;
  const count = `${rows.length} change${rows.length === 1 ? '' : 's'}`;
  const notable = rows.find(r => r.consequence === 'price') || rows.find(r => r.consequence === 'schedule');
  if (!notable) return count;
  const what = notable.kind === 'document' ? (notable.name || 'a document') : notable.label;
  return `${count} · including ${String(what).toLowerCase()}`;
}
